import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Settings as SettingsIcon, Home, MessageCircle, Folders, File, Loader2, Plus, Brain } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatRelativeDate } from '@/lib/formatRelativeDate'
import { Button } from '@/components/ui/Button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { ContextExplorer } from './ContextExplorer'
import { useChatSessionsStore } from '@/stores/chatSessionsStore'
import { useAuth } from '@/hooks/useAuth'
import type { Document } from '@/types/api'
import type { SidebarTab, ExplorerDoc } from './types'

interface SidebarProps {
  className?: string
  activeTab: SidebarTab
  onTabChange: (tab: SidebarTab) => void
  onSelectDoc: (doc: ExplorerDoc) => void
  onNewChat?: () => void
  documents?: Document[]
}

const navItems: { tab: SidebarTab; icon: typeof Home; label: string }[] = [
  { tab: 'add-context', icon: File, label: 'Add Context' },
  { tab: 'home', icon: Home, label: 'Home' },
  { tab: 'chats', icon: MessageCircle, label: 'Chats' },
  { tab: 'context', icon: Folders, label: 'Explorer' },
]

export function Sidebar({
  className,
  activeTab,
  onTabChange,
  onSelectDoc,
  onNewChat,
  documents,
}: SidebarProps) {
  const navigate = useNavigate()
  const { sessionId: activeSessionId } = useParams<{ sessionId?: string }>()
  const { sessions, loading, fetchSessions } = useChatSessionsStore()
  const { user } = useAuth()
  const [avatarError, setAvatarError] = useState(false)

  const initials = useMemo(() => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    }
    return (user?.email?.[0] || 'U').toUpperCase()
  }, [user?.displayName, user?.email])

  // Fetch sessions once on mount
  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  useEffect(() => {
    setAvatarError(false)
  }, [user?.uid, user?.photoURL])

  return (
    <div className={cn('flex flex-col h-full bg-sidebar border-r border-white/15', className)}>
      {/* Logo */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold text-lg">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-white shadow-lg shadow-primary/20">
            <Brain className="h-4 w-4" />
          </div>
          <span>ContextMD</span>
        </div>
      </div>

      {/* Nav Buttons */}
      <div className="px-2 mb-2 space-y-1">
        {navItems.map(({ tab, icon: Icon, label }) => (
          <button
            key={tab}
            onClick={() => onTabChange(tab)}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              activeTab === tab
                ? 'bg-tertiary text-foreground ring-1 ring-primary/40'
                : 'text-muted-foreground hover:bg-tertiary/80 hover:text-foreground'
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      <Separator className="my-2 bg-border/50" />

      {/* Conditional content below separator */}
      {activeTab === 'chats' && (
        <ScrollArea className="flex-1 px-4">
          <div className="space-y-4 pt-2">
            {/* New chat button */}
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start gap-2 text-xs border-border/50"
              onClick={onNewChat}
            >
              <Plus className="h-3.5 w-3.5" />
              New Chat
            </Button>

            {loading && sessions.length === 0 && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}

            {!loading && sessions.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">
                No conversations yet
              </p>
            )}

            {sessions.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground mb-2 px-2 uppercase tracking-wider">
                  Recent
                </h3>
                <div className="space-y-1">
                  {sessions.map((session) => (
                    <button
                      key={session.id}
                      onClick={() => navigate(`/chat/${session.id}`)}
                      className={cn(
                        'w-full text-left px-3 py-2 rounded-lg text-sm transition-all truncate',
                        session.id === activeSessionId
                          ? 'bg-tertiary text-foreground font-medium ring-1 ring-primary/30'
                          : 'text-foreground/80 hover:bg-tertiary/70 hover:text-foreground'
                      )}
                    >
                      <span className="block truncate">
                        {session.currentFocus || `Chat ${formatRelativeDate(session.createdAt)}`}
                      </span>
                      <span className="block text-[10px] text-muted-foreground mt-0.5">
                        {formatRelativeDate(session.updatedAt ?? session.createdAt)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      )}

      {activeTab === 'context' && (
        <ContextExplorer
          documents={documents}
          onSelectDoc={onSelectDoc}
        />
      )}

      {/* Settings */}
      <div className="mt-auto p-4 border-t border-border/50">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="flex-1 justify-start gap-2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => navigate('/settings')}
          >
            <SettingsIcon className="h-4 w-4" />
            Settings
          </Button>

          <div
            className="h-8 w-8 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary shrink-0 overflow-hidden"
            aria-label="User avatar"
          >
            {user?.photoURL && !avatarError ? (
              <img
                src={user.photoURL}
                alt={user.displayName || 'User'}
                className="h-full w-full object-cover"
                referrerPolicy="no-referrer"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <span>{initials}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
